import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ClockIcons,
  ListCheckIcons,
  SettingsIcons,
} from '../../../../icons/icons';
import { Button, MainLayout } from '../../../../ui';
import { NoDataState } from '../../../../ui/components/Common/NoData/NoDataState';
import { DatePicker } from '../../../../ui/input/date_picker/date_picker';
import { CandidateStatusEnum } from '../../../../utils/helpers/candidateStatus';
import { Tabs } from '../../../assessment/view/tabs/tabs';
import { AssessmentResults } from '../../../assessment/view/view_assessment/assessment_result_list/assessment_result_list';
import { Settings } from '../../../assessment/view/view_assessment/settings/settings';
import { CandidateInvites } from '../JobOpeningDetails/AutoAssessmentTab/TabContent/CandidateInvites';
import { InvitationTemplate } from '../JobOpeningDetails/AutoAssessmentTab/TabContent/InvitationTemplate';
import { AutoAssessmentResultsDetails } from './Containers/AssesmentResultsDetails';
import { useAssessmentQuestions } from './Hooks';
const AssessmentQuestions = () => {
  const location = useLocation();
  const getAssessmentTabFromUrl = (): number => {
    const searchParams = new URLSearchParams(location.search);
    const tabValue = searchParams.get('assessmentTab');
    const tabIndex = parseInt(tabValue || '0');
    return isNaN(tabIndex) ? 0 : tabIndex;
  };

  const getSidebarTabFromUrl = (): 'Config' | 'Results' => {
    const searchParams = new URLSearchParams(location.search);
    return (searchParams.get('sidebarTab') as 'Config' | 'Results') || 'Config';
  };

  const {
    activeTab,
    setActiveTab: hookSetActiveTab,
    sidebarTab,
    setSidebarTab,
    tabTitles,
    autoAssessment,
    assessmentUserListResponse,
    overviewResultResponse,
    highestResultScore,
    updateAssessmentUserList,
    assessmentState,
    assessmentFormInfo,
    changeDate,
    handleDataChange,
    // Invite Candidates
    candidatesList,
    selectedCandidates,
    selectedCandidateIds,
    limit,
    loading,
    sort,
    sendInvites,
    handleRemoveCandidate,
    handleSortChange,
    isRowSelected,
    handleSelectRow,
    handleSelectAll,
    handleStatusListChange,
    onPageChange,
    isAllSelected,
    isIndeterminate,
    pageNumber,
    totalPages,
    totalRecords,
    candidateInviteObject,
    setLocalName,
    setLocalEmail,
    localName,
    localEmail,
  } = useAssessmentQuestions();

  // URL-sync wrapper for setActiveTab
  const setActiveTab = (tabIndex: number | string) => {
    const index =
      typeof tabIndex === 'string' ? tabTitles.indexOf(tabIndex) : tabIndex;
    if (index === -1) return;

    hookSetActiveTab(index);
    const searchParams = new URLSearchParams(location.search);

    // Always ensure main tab is set correctly for Auto-Assessments
    searchParams.set('tab', 'Auto-Assessments');
    searchParams.set('assessmentTab', index.toString());
    searchParams.set('sidebarTab', sidebarTab);

    const newUrl = `${location.pathname}?${searchParams.toString()}`;
    window.history.pushState(null, '', newUrl);
  };

  const handleSidebarChange = (tab: 'Config' | 'Results') => {
    setSidebarTab(tab);
    hookSetActiveTab(0); // Reset sub-tab when switching sidebar

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', 'Auto-Assessments');
    searchParams.set('sidebarTab', tab);
    searchParams.set('assessmentTab', '0');

    const newUrl = `${location.pathname}?${searchParams.toString()}`;
    window.history.pushState(null, '', newUrl);
  };

  const updateEmailTemplateState = (_updatedJsonTemplate: string) => {
    // This could update local state if needed, but the hook handles fetch
  };

  // Sync with URL on location change
  React.useEffect(() => {
    const tabFromUrl = getAssessmentTabFromUrl();
    const sidebarTabFromUrl = getSidebarTabFromUrl();

    if (tabFromUrl !== activeTab) {
      hookSetActiveTab(tabFromUrl);
    }

    if (sidebarTabFromUrl !== sidebarTab) {
      setSidebarTab(sidebarTabFromUrl);
    }
  }, [location.search]);

  const [selectedResult, setSelectedResult] =
    useState<{
      assessmentId: string;
      userId: string;
      id: string;
    } | null>(null);

  const sidebarButtons = useMemo(
    () => [
      {
        label: 'Config',
        disabled: false,
        Icon: SettingsIcons,
      },
      {
        label: 'Results',
        disabled: false,
        Icon: ListCheckIcons,
      },
    ],
    [],
  );

  const renderTabContent = () => {
    const currentTabTitle = tabTitles[activeTab];
    if (
      !autoAssessment?.assessment?.assessmentType ||
      autoAssessment?.assessment?.assessmentType !== 'candidate' ||
      !autoAssessment?.assessment?.id
    ) {
      return null;
    }

    const baseProps = {
      preScreenQuestions: autoAssessment?.assessment?.preScreen
        ? autoAssessment?.assessment.preScreen
        : [],
      assessmentId: autoAssessment.assessment.id,
      userResults: assessmentUserListResponse?.assessmentUserList || [],
      completionRatio: assessmentUserListResponse?.completionRatio || '',
      highestScore: highestResultScore || 0,
      updateAssessmentUserList,
      behaviorSkills: overviewResultResponse?.behaviorSkills || [],
      skills: overviewResultResponse?.skills || [],
      countCompleted: assessmentUserListResponse?.countCompleted || 0,
      countTotal: assessmentUserListResponse?.countTotal || 0,
      isAutoAssessment: true,
      onResultClick: (assessmentResult: any) => {
        setSelectedResult({
          assessmentId: assessmentResult.assessmentId,
          userId: assessmentResult.userId,
          id: assessmentResult.id,
        });
      },
    };

    //

    switch (currentTabTitle) {
      case 'Settings':
        return (
          <>
            { assessmentFormInfo && (
              <div className='mb-3 border-b border-slate-100'>
                <div className='flex flex-col justify-between gap-3 px-1 lg:flex-row lg:items-center'>
                  <div className='flex items-center gap-3'>
                    <div className='w-8 h-8 bg-slate-50 rounded-lg flex items-center justify-center shrink-0 border border-slate-100'>
                      <ClockIcons className='w-4 h-4 text-slate-600' />
                    </div>
                    <div>
                      <h3 className='text-sm font-semibold text-slate-900'>
                        Assessment Availability
                      </h3>
                      <p className='text-xs text-slate-500'>
                        Specify the dates when this assessment will be
                        accessible.
                      </p>
                    </div>
                  </div>

                  <div className='flex flex-wrap items-center gap-2'>
                    <div className='w-full sm:w-[250px]'>
                      <DatePicker
                          startDate={assessmentFormInfo.startDate}
                          endDate={assessmentFormInfo.endDate}
                          onChange={changeDate}
                      />
                    </div>
                    <Button
                        variant='primary'
                        label='Update'
                        onClick={handleDataChange}
                        className='!h-8 !px-4 !rounded-lg text-xs font-bold'
                    />
                  </div>
                </div>
              </div>
            ) }
            <div className='h-0.5 w-full rounded-full bg-slate-200 my-1' />
            <Settings
                assessmentType={'candidate'}
                assessmentId={autoAssessment.assessment.id}
                skipSpecificSettings={true}
                setActiveTab={setActiveTab as any}
            />
          </>
        );
      case 'Invitation Template':
        return (
          <InvitationTemplate
              setActiveTab={setActiveTab as any}
              emailTemplate={autoAssessment.emailJsonTemplate}
              updateEmailTemplateState={updateEmailTemplateState}
          />
        );
      case 'Candidates':
        return (
          <CandidateInvites
              handleSendInvites={sendInvites}
              handleRemoveCandidate={handleRemoveCandidate}
              candidatesList={candidatesList}
              loading={loading}
              pageNumber={pageNumber}
              totalPages={totalPages}
              totalRecords={totalRecords}
              selectedCandidates={selectedCandidates}
              selectedCandidateIds={selectedCandidateIds}
              limit={limit}
              handleSortChange={handleSortChange}
              isRowSelected={isRowSelected}
              candidateInviteObject={candidateInviteObject}
              handleSelectRow={handleSelectRow}
              sort={sort}
              handleSelectAll={handleSelectAll}
              handleStatusListChange={handleStatusListChange}
              onPageChange={onPageChange}
              setLocalName={setLocalName}
              setLocalEmail={setLocalEmail}
              localName={localName}
              localEmail={localEmail}
              isAllSelected={isAllSelected}
              isIndeterminate={isIndeterminate}
          />
        );
      case 'Results':
        if (!assessmentState.hasData) {
          return (
            <NoDataState
                title='No Assessment Results Yet'
                message='Start by inviting candidates to take the assessment. Results will appear here once candidates begin completing their assessments.'
                illustration='assessment'
            />
          );
        }
        return <AssessmentResults {...baseProps} />;

      case CandidateStatusEnum.WAITLISTED:
        if (!assessmentState.hasData) {
          return (
            <NoDataState
                title='No Assessment Results Yet'
                message='Start by inviting candidates to take the assessment. Results will appear here once candidates begin completing their assessments.'
                illustration='assessment'
            />
          );
        }
        return (
          <AssessmentResults
              {...baseProps}
              candidateStatus={CandidateStatusEnum.WAITLISTED}
          />
        );

      case CandidateStatusEnum.SHORTLISTED:
        if (!assessmentState.hasData) {
          return (
            <NoDataState
                title='No Assessment Results Yet'
                message='Start by inviting candidates to take the assessment. Results will appear here once candidates begin completing their assessments.'
                illustration='assessment'
            />
          );
        }
        return (
          <AssessmentResults
              {...baseProps}
              candidateStatus={CandidateStatusEnum.SHORTLISTED}
          />
        );

      case CandidateStatusEnum.UNSUCCESSFUL:
        if (!assessmentState.hasData) {
          return (
            <NoDataState
                title='No Assessment Results Yet'
                message='Start by inviting candidates to take the assessment. Results will appear here once candidates begin completing their assessments.'
                illustration='assessment'
            />
          );
        }
        return (
          <AssessmentResults
              {...baseProps}
              candidateStatus={CandidateStatusEnum.UNSUCCESSFUL}
          />
        );

      default:
        return null;
    }
  };
  if (assessmentState.isLoading) {
    return (
      <NoDataState
          title='Loading Assessment Results'
          message='Please wait while we fetch the assessment data...'
          illustration='loading'
      />
    );
  }

  if (assessmentState.error) {
    return (
      <NoDataState
          title='Unable to Load Assessment Data'
          message={assessmentState.error}
          actionLabel='Retry'
          onAction={() => window.location.reload()}
          illustration='error'
      />
    );
  }

  return (
    <div className='mt-3 flex flex-col gap-3 sm:mt-5 lg:flex-row lg:gap-6'>
      { !selectedResult && (
        <div className='flex w-full gap-1.5 overflow-x-auto rounded-lg border border-slate-100 bg-slate-50/70 p-1.5 lg:sticky lg:top-4 lg:h-fit lg:w-16 lg:shrink-0 lg:flex-col lg:gap-2 lg:overflow-visible lg:rounded-2xl lg:py-3'>
          { sidebarButtons.map((button, index) => {
            if (button.disabled) return null;
            const isActive = sidebarTab === button.label;
            const Icon = button.Icon;
            return (
              <button
                  key={index}
                  onClick={() => handleSidebarChange(button.label as any)}
                  className='group flex min-w-fit flex-row items-center gap-1.5 px-1 transition-all duration-200 lg:min-w-0 lg:flex-col lg:gap-0 lg:px-0'
              >
                <div
                    className={`w-7 h-7 rounded-lg flex shrink-0 items-center justify-center transition-all duration-300 border lg:w-9 lg:h-9 lg:rounded-xl ${
                      isActive
                        ? 'bg-emerald-600 border-emerald-600 text-white shadow-lg shadow-emerald-100 lg:scale-105'
                        : 'bg-white border-slate-200 text-slate-400 hover:border-emerald-400 hover:text-emerald-600 group-hover:shadow-md'
                    }`}
                >
                  <Icon
                      className={`w-3.5 h-3.5 lg:w-4 lg:h-4 ${
                        isActive ? 'text-white' : 'text-slate-400'
                      }`}
                  />
                </div>
                <p
                    className={`text-[9px] font-bold uppercase tracking-tight text-left leading-tight transition-colors lg:mt-1 lg:text-center lg:text-[7.5px] ${
                      isActive
                        ? 'text-emerald-600'
                        : 'text-slate-400 group-hover:text-emerald-500'
                    }`}
                >
                  { button.label }
                </p>
              </button>
            );
          }) }
        </div>
      ) }
      <MainLayout>
        { selectedResult ? (
          <AutoAssessmentResultsDetails
              assessmentId={selectedResult.assessmentId}
              userId={selectedResult.userId}
              id={selectedResult.id}
              backToClick={() => setSelectedResult(null)}
          />
        ) : (
          <>
            <Tabs
                tabTitles={tabTitles}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
            />
            { renderTabContent() }
          </>
        ) }
      </MainLayout>
    </div>
  );
};

export default AssessmentQuestions;
